import axios from 'axios';
import { API_BASE_URL } from '../constants';

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Settings service (paramètres publics)
export const settingsService = {
    // Get public settings for display on website
    getPublicSettings: async (cityCode) => {
        try {
            const url = cityCode ? `/website/public-settings?city=${cityCode}` : '/website/public-settings';
            const response = await api.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching public settings:', error);
            // Retourner des valeurs par défaut en cas d'erreur
            return {
                success: false,
                data: {
                    display: {
                        bonusPremiereRecharge: '50%',
                        pointsNouveau: '2 000 pts',
                        pointsAncien: '1 000 pts',
                        cashbackRecharge: '3%',
                        bonusHeures: '2 000 pts',
                        seuilHeures: '130h',
                        bonusCourses: '2 000 pts',
                        seuilCourses: '55 courses',
                        bonusLundi: '2 000 pts',
                        montantMinLundi: '10 000 F',
                        bonusMaxChallenge: '4 000 pts'
                    },
                    raw: {}
                }
            };
        }
    },
};

// Banner service
export const bannerService = {
    // Get all active banners
    getAll: async () => {
        try {
            const response = await api.get('/website/banners');
            return response.data;
        } catch (error) {
            console.error('Error fetching banners:', error);
            return { success: false, data: { hero: [], middle: [], bottom: [] } };
        }
    },

    // Get banners by position
    getByPosition: async (position) => {
        try {
            const response = await api.get(`/website/banners?position=${position}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching ${position} banners:`, error);
            return { success: false, data: [] };
        }
    },
};

// Contact service
export const contactService = {
    // Send contact form message
    sendMessage: async (data) => {
        try {
            const response = await api.post('/website/contact', data);
            return response.data;
        } catch (error) {
            console.error('Error sending contact message:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de l\'envoi du message' };
        }
    },
};

// City service
export const cityService = {
    // Get all active cities
    getActiveCities: async () => {
        try {
            const response = await api.get('/website/cities');
            return response.data;
        } catch (error) {
            console.error('Error fetching cities:', error);
            return { success: false, data: [] };
        }
    },
};

// Affiliate service
export const affiliateService = {
    // Submit application to become an affiliate
    submitApplication: async (data) => {
        try {
            const response = await api.post('/website/affiliates/apply', data);
            return response.data;
        } catch (error) {
            console.error('Error submitting application:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de la soumission' };
        }
    },

    // Check application status
    checkApplicationStatus: async (phone) => {
        try {
            const response = await api.get(`/website/affiliates/application-status?phone=${phone}`);
            return response.data;
        } catch (error) {
            console.error('Error checking status:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de la vérification' };
        }
    },

    // Login as affiliate
    login: async (phone, password) => {
        try {
            const response = await api.post('/website/affiliates/login', { phone, password });
            return response.data;
        } catch (error) {
            console.error('Error logging in:', error);
            throw error.response?.data || { success: false, message: 'Erreur de connexion' };
        }
    },

    // Validate referral code
    validateCode: async (code) => {
        try {
            const response = await api.get(`/website/affiliates/validate-code/${code}`);
            return response.data;
        } catch (error) {
            console.error('Error validating code:', error);
            return { success: false, valid: false };
        }
    },

    // Get dashboard (requires auth)
    getDashboard: async (token) => {
        try {
            const response = await api.get('/website/affiliates/dashboard', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching dashboard:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get referred drivers (requires auth)
    getReferrals: async (token, params = {}) => {
        try {
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/affiliates/referrals${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching referrals:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get commissions (requires auth)
    getCommissions: async (token, params = {}) => {
        try {
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/affiliates/commissions${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching commissions:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Update profile (requires auth)
    updateProfile: async (token, data) => {
        try {
            const response = await api.put('/website/affiliates/profile', data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de la mise à jour' };
        }
    },

    // Update password (requires auth)
    updatePassword: async (token, currentPassword, newPassword) => {
        try {
            const response = await api.put('/website/affiliates/password', { currentPassword, newPassword }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error updating password:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de la mise à jour' };
        }
    },

    // Register a driver (requires auth)
    registerDriver: async (token, data) => {
        try {
            const response = await api.post('/website/affiliates/register-driver', data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error registering driver:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de l\'enregistrement' };
        }
    },

    // Get commissions dashboard (requires auth)
    getCommissionsDashboard: async (token) => {
        try {
            const response = await api.get('/website/affiliates/commissions/dashboard', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching commissions dashboard:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get commissions history (requires auth)
    getCommissionsHistory: async (token, params = {}) => {
        try {
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/affiliates/commissions/history${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching commissions history:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get drivers with progress (requires auth)
    getDriversWithProgress: async (token, params = {}) => {
        try {
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/affiliates/drivers-progress${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching drivers progress:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get specific driver progress (requires auth)
    getDriverProgress: async (token, driverId) => {
        try {
            const response = await api.get(`/website/affiliates/drivers-progress/${driverId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching driver progress:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get same day bonuses (requires auth)
    getSameDayBonuses: async (token, params = {}) => {
        try {
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/affiliates/same-day-bonuses${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching same day bonuses:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get monthly retentions (requires auth)
    getMonthlyRetentions: async (token, params = {}) => {
        try {
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/affiliates/monthly-retentions${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching monthly retentions:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get 130h revenues grouped by month (requires auth)
    getRevenues130h: async (token) => {
        try {
            const response = await api.get(`/website/affiliates/revenues-130h`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching 130h revenues:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement des revenus 130h' };
        }
    },

    // Get collected revenues (paid commissions) (requires auth)
    getCollectedRevenues: async (token, params = {}) => {
        try {
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/affiliates/revenues/collected${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching collected revenues:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement des revenus encaissés' };
        }
    },
};

// Ambassador service (Brand Ambassador / Fleet owners)
export const ambassadorService = {
    // Submit application to become an ambassador
    submitApplication: async (data) => {
        try {
            const response = await api.post('/website/ambassadors/apply', data);
            return response.data;
        } catch (error) {
            console.error('Error submitting ambassador application:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de la soumission' };
        }
    },

    // Login as ambassador
    login: async (phone, password) => {
        try {
            const response = await api.post('/website/ambassadors/login', { phone, password });
            return response.data;
        } catch (error) {
            console.error('Error logging in as ambassador:', error);
            throw error.response?.data || { success: false, message: 'Erreur de connexion' };
        }
    },

    // Validate ambassador code
    validateCode: async (code) => {
        try {
            const response = await api.get(`/website/ambassadors/validate-code/${code}`);
            return response.data;
        } catch (error) {
            console.error('Error validating ambassador code:', error);
            return { success: false, valid: false };
        }
    },

    // Get dashboard (requires auth)
    getDashboard: async () => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const response = await api.get('/website/ambassadors/dashboard', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching ambassador dashboard:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get affiliates (requires auth)
    getAffiliates: async (params = {}) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/ambassadors/affiliates${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching affiliates:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get affiliate details with referred drivers (requires auth)
    getAffiliateDetails: async (affiliateId) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const response = await api.get(`/website/ambassadors/affiliates/${affiliateId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching affiliate details:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Register an affiliate (requires auth)
    registerAffiliate: async (data) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const response = await api.post('/website/ambassadors/affiliates', data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error registering affiliate:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de la creation' };
        }
    },

    // Get drivers (requires auth)
    getDrivers: async (params = {}) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/ambassadors/drivers${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching drivers:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Update profile (requires auth)
    updateProfile: async (data) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const response = await api.put('/website/ambassadors/profile', data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error updating ambassador profile:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de la mise a jour' };
        }
    },

    // Update password (requires auth)
    updatePassword: async (currentPassword, newPassword) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const response = await api.put('/website/ambassadors/password', { currentPassword, newPassword }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error updating ambassador password:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de la mise a jour' };
        }
    },

    // Register a driver (requires auth)
    registerDriver: async (data) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const response = await api.post('/website/ambassadors/drivers', data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error registering driver:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors de l\'enregistrement' };
        }
    },

    // Get revenues dashboard (requires auth)
    getRevenuesDashboard: async () => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const response = await api.get('/website/ambassadors/revenues/dashboard', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching revenues dashboard:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get pending revenues (requires auth)
    getPendingRevenues: async (params = {}) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/ambassadors/revenues/pending${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching pending revenues:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get collected revenues (requires auth)
    getCollectedRevenues: async (params = {}) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/ambassadors/revenues/collected${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching collected revenues:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get driver revenue details (requires auth)
    getDriverRevenueDetails: async (driverId) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const response = await api.get(`/website/ambassadors/revenues/driver/${driverId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching driver revenue details:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get 130h revenues for ambassador (requires auth) - all months
    getRevenues130h: async () => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const response = await api.get(`/website/ambassadors/revenues/130h`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching 130h revenues:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement des revenus 130h' };
        }
    },

    // Get monthly retentions for ambassador (requires auth)
    getMonthlyRetentions: async (params = {}) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/ambassadors/monthly-retentions${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching monthly retentions:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get direct drivers (requires auth)
    getDirectDrivers: async (params = {}) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/ambassadors/drivers/direct${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching direct drivers:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get indirect drivers (requires auth)
    getIndirectDrivers: async (params = {}) => {
        try {
            const token = localStorage.getItem('ambassadorToken');
            const queryParams = new URLSearchParams(params).toString();
            const response = await api.get(`/website/ambassadors/drivers/indirect${queryParams ? `?${queryParams}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching indirect drivers:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },
};

// Driver service (Ranking)
export const driverService = {
    // Login as driver
    login: async (phone, password) => {
        try {
            const response = await api.post('/website/drivers/login', { phone, password });
            return response.data;
        } catch (error) {
            console.error('Error logging in as driver:', error);
            throw error.response?.data || { success: false, message: 'Erreur de connexion' };
        }
    },

    // Get cities available for ranking
    getCities: async () => {
        try {
            const response = await api.get('/website/drivers/cities');
            return response.data;
        } catch (error) {
            console.error('Error fetching cities:', error);
            return { success: false, data: { cities: [] } };
        }
    },

    // Get public ranking (Top 15)
    getRanking: async (period = null, city = null) => {
        try {
            const params = new URLSearchParams();
            if (period) params.append('period', period);
            if (city) params.append('city', city);
            const queryString = params.toString();
            const url = queryString
                ? `/website/drivers/ranking?${queryString}`
                : '/website/drivers/ranking';
            const response = await api.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching ranking:', error);
            return { success: false, data: { rankings: [], availablePeriods: [] } };
        }
    },

    // Get my ranking (requires auth)
    getMyRanking: async (token, period = null, city = null) => {
        try {
            const params = new URLSearchParams();
            if (period) params.append('period', period);
            if (city) params.append('city', city);
            const queryString = params.toString();
            const url = queryString
                ? `/website/drivers/my-ranking?${queryString}`
                : '/website/drivers/my-ranking';
            const response = await api.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching my ranking:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },

    // Get my profile (requires auth)
    getProfile: async (token) => {
        try {
            const response = await api.get('/website/drivers/profile', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching driver profile:', error);
            throw error.response?.data || { success: false, message: 'Erreur lors du chargement' };
        }
    },
};

export default api;
